<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  collection,
  doc,
  addDoc,
  setDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { uploadBytesResumable, getDownloadURL, ref as storageRef } from 'firebase/storage'
import { db, storage } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'
import { useIncidentStore } from '@/stores/incident'
import { useToastStore } from '@/stores/toast'
import { useRosterStore } from '@/stores/roster'
import { IncidentStatus } from '@/types'
import type { User, Incident, AgencyChannelPreset, ObjectiveTemplate, AgencyHazardTemplate, AgencyInvite, AgencyDomainRule, AgencyJoinRequest } from '@/types'
import UserAvatar from '@/components/common/UserAvatar.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import D4HRosterPanel from '@/components/agency/D4HRosterPanel.vue'
import AgencyMembersTable from '@/components/agency/AgencyMembersTable.vue'
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItemIndicator,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from 'radix-vue'

const router = useRouter()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()
const incidentStore = useIncidentStore()
const toast = useToastStore()
const rosterStore = useRosterStore()

const userRole = computed(() => authStore.userProfile?.role)
const isAdmin = computed(() => permissionsStore.can('agency.logo_upload', userRole.value))
const agencyId = computed(() => authStore.userProfile?.agency_id ?? '')
const currentUserId = computed(() => authStore.currentUser?.uid ?? '')

// ── Settings tab ──────────────────────────────────────────────────────────────
const agencyName = ref('')
const agencyLogo = ref('')
const customQuals = ref<string[]>([])
const d4hWebUrl = ref('')
const savingName = ref(false)
const savingQuals = ref(false)
const uploadingLogo = ref(false)
const logoFileInput = ref<HTMLInputElement | null>(null)
const newQualInput = ref('')

let agencyUnsub: Unsubscribe | null = null

function subscribeToAgency() {
  if (!agencyId.value) return
  agencyUnsub = onSnapshot(doc(db, 'agencies', agencyId.value), (snap) => {
    if (snap.exists()) {
      const d = snap.data()
      agencyName.value = d.agency_name ?? ''
      agencyLogo.value = d.agency_logo ?? ''
      customQuals.value = d.custom_quals ?? []
      d4hWebUrl.value = d.d4h_web_url ?? ''
    }
  })
}

async function saveName() {
  if (!agencyId.value) return
  savingName.value = true
  try {
    await updateDoc(doc(db, 'agencies', agencyId.value), { agency_name: agencyName.value })
    toast.add('Agency name updated', 'success')
  } finally {
    savingName.value = false
  }
}

async function saveQuals() {
  if (!agencyId.value) return
  savingQuals.value = true
  try {
    await updateDoc(doc(db, 'agencies', agencyId.value), { custom_quals: customQuals.value })
    toast.add('Custom quals updated', 'success')
  } finally {
    savingQuals.value = false
  }
}

function addQual() {
  const v = newQualInput.value.trim()
  if (!v || customQuals.value.includes(v)) return
  customQuals.value = [...customQuals.value, v]
  newQualInput.value = ''
}

function removeQual(qual: string) {
  customQuals.value = customQuals.value.filter(q => q !== qual)
}

async function onLogoFileChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    toast.add('Only JPEG and PNG images are accepted', 'error', 4000)
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    toast.add('Maximum file size is 10 MB', 'error', 4000)
    return
  }

  const ext = file.type === 'image/png' ? 'png' : 'jpg'
  const path = `agencies/${agencyId.value}/logo.${ext}`

  uploadingLogo.value = true
  try {
    const fileRef = storageRef(storage, path)
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type })
    await new Promise<void>((resolve, reject) => {
      uploadTask.on('state_changed', null, reject, resolve)
    })
    const logoUrl = await getDownloadURL(uploadTask.snapshot.ref)
    await updateDoc(doc(db, 'agencies', agencyId.value), { agency_logo: logoUrl })
    if (logoFileInput.value) logoFileInput.value.value = ''
    toast.add('Logo updated', 'success')
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error')
  } finally {
    uploadingLogo.value = false
  }
}

// ── Channel presets ────────────────────────────────────────────────────────────
const channelPresets = ref<AgencyChannelPreset[]>([])
let presetsUnsub: Unsubscribe | null = null

function subscribeToPresets() {
  if (!agencyId.value) return
  presetsUnsub?.()
  presetsUnsub = onSnapshot(
    collection(db, 'agencies', agencyId.value, 'channel_presets'),
    (snap) => {
      channelPresets.value = snap.docs
        .map((d) => ({ preset_id: d.id, ...d.data() } as AgencyChannelPreset))
        .sort((a, b) => a.order - b.order)
    },
    () => {
      presetsUnsub = null
      setTimeout(subscribeToPresets, 2000)
    },
  )
}

const CHANNEL_MODE_OPTIONS = [
  { label: 'Analog', value: 'A' },
  { label: 'Digital', value: 'D' },
  { label: 'Mixed', value: 'M' },
]

const showAddPresetForm = ref(false)
const editingPresetId = ref<string | null>(null)
const presetForm = ref({
  name: '',
  rx_freq: '',
  rx_tone: '',
  tx_freq: '',
  tx_tone: '',
  mode: 'A',
  remarks: '',
})

function openAddPreset() {
  editingPresetId.value = null
  presetForm.value = { name: '', rx_freq: '', rx_tone: '', tx_freq: '', tx_tone: '', mode: 'A', remarks: '' }
  showAddPresetForm.value = true
}

function openEditPreset(preset: AgencyChannelPreset) {
  editingPresetId.value = preset.preset_id
  presetForm.value = {
    name: preset.name,
    rx_freq: preset.rx_freq,
    rx_tone: preset.rx_tone,
    tx_freq: preset.tx_freq,
    tx_tone: preset.tx_tone,
    mode: preset.mode,
    remarks: preset.remarks,
  }
  showAddPresetForm.value = true
}

function cancelPresetForm() {
  showAddPresetForm.value = false
  editingPresetId.value = null
}

async function savePreset() {
  if (!agencyId.value || !presetForm.value.name.trim()) return
  const presetsRef = collection(db, 'agencies', agencyId.value, 'channel_presets')
  if (editingPresetId.value) {
    await updateDoc(doc(presetsRef, editingPresetId.value), { ...presetForm.value })
  } else {
    await addDoc(presetsRef, {
      ...presetForm.value,
      order: channelPresets.value.length,
      created_at: serverTimestamp(),
    })
  }
  showAddPresetForm.value = false
  editingPresetId.value = null
}

function confirmDeletePreset(preset: AgencyChannelPreset) {
  if (window.confirm(`Delete the "${preset.name}" preset? This cannot be undone.`)) {
    deletePreset(preset.preset_id)
  }
}

async function deletePreset(presetId: string) {
  if (!agencyId.value) return
  await deleteDoc(doc(db, 'agencies', agencyId.value, 'channel_presets', presetId))
}

// ── Objective templates ────────────────────────────────────────────────────────
const objectiveTemplates = ref<ObjectiveTemplate[]>([])
let objectivesUnsub: Unsubscribe | null = null

const MISSION_TYPE_OPTIONS = [
  { label: 'Lost Person', value: 'lost_person' },
  { label: 'Rescue', value: 'rescue' },
  { label: 'Recovery', value: 'recovery' },
  { label: 'Other', value: 'other' },
]

function subscribeToObjectiveTemplates() {
  if (!agencyId.value) return
  objectivesUnsub?.()
  objectivesUnsub = onSnapshot(
    query(
      collection(db, 'agencies', agencyId.value, 'objective_templates'),
      orderBy('order', 'asc'),
    ),
    (snap) => {
      objectiveTemplates.value = snap.docs.map((d) => ({ id: d.id, ...d.data() } as ObjectiveTemplate))
    },
    () => {
      objectivesUnsub = null
      setTimeout(subscribeToObjectiveTemplates, 2000)
    },
  )
}

const showAddObjectiveForm = ref(false)
const editingObjectiveId = ref<string | null>(null)
const objectiveForm = ref({ text: '', mission_types: [] as string[] })

function openAddObjective() {
  editingObjectiveId.value = null
  objectiveForm.value = { text: '', mission_types: [] }
  showAddObjectiveForm.value = true
}

function openEditObjective(t: ObjectiveTemplate) {
  editingObjectiveId.value = t.id
  objectiveForm.value = { text: t.text, mission_types: [...t.mission_types] }
  showAddObjectiveForm.value = true
}

function cancelObjectiveForm() {
  showAddObjectiveForm.value = false
  editingObjectiveId.value = null
}

function toggleObjectiveMissionType(value: string) {
  const idx = objectiveForm.value.mission_types.indexOf(value)
  if (idx === -1) objectiveForm.value.mission_types.push(value)
  else objectiveForm.value.mission_types.splice(idx, 1)
}

async function saveObjective() {
  if (!agencyId.value || !objectiveForm.value.text.trim()) return
  const ref = collection(db, 'agencies', agencyId.value, 'objective_templates')
  if (editingObjectiveId.value) {
    await updateDoc(doc(ref, editingObjectiveId.value), {
      text: objectiveForm.value.text.trim(),
      mission_types: objectiveForm.value.mission_types,
    })
  } else {
    await addDoc(ref, {
      text: objectiveForm.value.text.trim(),
      mission_types: objectiveForm.value.mission_types,
      order: objectiveTemplates.value.length,
      created_at: serverTimestamp(),
    })
  }
  showAddObjectiveForm.value = false
  editingObjectiveId.value = null
}

function confirmDeleteObjective(t: ObjectiveTemplate) {
  if (window.confirm(`Delete the objective template "${t.text}"? This cannot be undone.`)) {
    deleteObjective(t.id)
  }
}

async function deleteObjective(id: string) {
  if (!agencyId.value) return
  await deleteDoc(doc(db, 'agencies', agencyId.value, 'objective_templates', id))
}

function missionTypeLabel(value: string) {
  return MISSION_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value
}

// ── Hazard templates ──────────────────────────────────────────────────────────
const hazardTemplates = ref<AgencyHazardTemplate[]>([])
let hazardsUnsub: Unsubscribe | null = null

function subscribeToHazardTemplates() {
  if (!agencyId.value) return
  hazardsUnsub?.()
  hazardsUnsub = onSnapshot(
    query(
      collection(db, 'agencies', agencyId.value, 'hazard_templates'),
      orderBy('order', 'asc'),
    ),
    (snap) => {
      hazardTemplates.value = snap.docs.map((d) => ({ template_id: d.id, ...d.data() } as AgencyHazardTemplate))
    },
  )
}

const showAddHazardForm = ref(false)
const editingHazardId = ref<string | null>(null)
const hazardForm = ref({ hazard: '', mitigation: '' })

function openAddHazard() {
  editingHazardId.value = null
  hazardForm.value = { hazard: '', mitigation: '' }
  showAddHazardForm.value = true
}

function openEditHazard(t: AgencyHazardTemplate) {
  editingHazardId.value = t.template_id
  hazardForm.value = { hazard: t.hazard, mitigation: t.mitigation }
  showAddHazardForm.value = true
}

function cancelHazardForm() {
  showAddHazardForm.value = false
  editingHazardId.value = null
}

async function saveHazardTemplate() {
  if (!agencyId.value || !hazardForm.value.hazard.trim()) return
  const ref = collection(db, 'agencies', agencyId.value, 'hazard_templates')
  if (editingHazardId.value) {
    await updateDoc(doc(ref, editingHazardId.value), {
      hazard: hazardForm.value.hazard.trim(),
      mitigation: hazardForm.value.mitigation.trim(),
    })
  } else {
    await addDoc(ref, {
      hazard: hazardForm.value.hazard.trim(),
      mitigation: hazardForm.value.mitigation.trim(),
      order: hazardTemplates.value.length,
      created_at: serverTimestamp(),
    })
  }
  showAddHazardForm.value = false
  editingHazardId.value = null
}

function confirmDeleteHazard(t: AgencyHazardTemplate) {
  if (window.confirm(`Delete the hazard template "${t.hazard}"? This cannot be undone.`)) {
    deleteHazardTemplate(t.template_id)
  }
}

async function deleteHazardTemplate(id: string) {
  if (!agencyId.value) return
  await deleteDoc(doc(db, 'agencies', agencyId.value, 'hazard_templates', id))
}

// ── Members tab ───────────────────────────────────────────────────────────────
const members = ref<User[]>([])
let membersUnsub: Unsubscribe | null = null

function subscribeToMembers() {
  if (!agencyId.value) return
  const q = query(collection(db, 'users'), where('agency_id', '==', agencyId.value))
  membersUnsub = onSnapshot(q, (snap) => {
    members.value = snap.docs.map((d) => ({ user_id: d.id, ...d.data() } as User))
  })
}

function memberFullName(m: User) {
  return [m.user_first_name, m.user_last_name].filter(Boolean).join(' ')
}

async function changeRole(member: User, newRole: string) {
  try {
    await updateDoc(doc(db, 'users', member.user_id), { role: newRole })
    toast.add(`${memberFullName(member)} is now ${newRole === 'admin' ? 'an admin' : 'a member'}`, 'success')
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error')
  }
}

// Deactivate confirm dialog
const deactivateDialogOpen = ref(false)
const pendingDeactivateMember = ref<User | null>(null)

function confirmDeactivate(member: User) {
  pendingDeactivateMember.value = member
  deactivateDialogOpen.value = true
}

async function deactivate(member: User) {
  await updateDoc(doc(db, 'users', member.user_id), { is_active: false })
}

async function handleDeactivateConfirm() {
  if (pendingDeactivateMember.value) {
    await deactivate(pendingDeactivateMember.value)
  }
  deactivateDialogOpen.value = false
  pendingDeactivateMember.value = null
}

async function reactivate(member: User) {
  await updateDoc(doc(db, 'users', member.user_id), { is_active: true })
}

// ── Pending Invites ───────────────────────────────────────────────────────────
const pendingInvites = ref<AgencyInvite[]>([])
const inviteEmail = ref('')
const sendingInvite = ref(false)
let invitesUnsub: Unsubscribe | null = null

function subscribeToPendingInvites() {
  if (!agencyId.value) return
  const q = query(
    collection(db, 'agency_invites'),
    where('agency_id', '==', agencyId.value),
    where('claimed_by_uid', '==', null),
  )
  invitesUnsub = onSnapshot(q, (snap) => {
    const now = Timestamp.now()
    pendingInvites.value = snap.docs
      .map(d => ({ invite_id: d.id, ...d.data() } as AgencyInvite))
      .filter(inv => inv.expires_at.toMillis() > now.toMillis())
      .sort((a, b) => b.created_at.toMillis() - a.created_at.toMillis())
  })
}

// Called with an email from the members list (D4H-only rows), else uses the input
async function sendInvite(emailArg?: string) {
  const email = (typeof emailArg === 'string' ? emailArg : inviteEmail.value).trim().toLowerCase()
  if (!email || !agencyId.value) return
  sendingInvite.value = true
  try {
    const inviteRef = doc(collection(db, 'agency_invites'))
    const now = Timestamp.now()
    const expiresAt = Timestamp.fromMillis(now.toMillis() + 7 * 24 * 60 * 60 * 1000)
    await setDoc(inviteRef, {
      invite_id: inviteRef.id,
      agency_id: agencyId.value,
      email,
      invited_by_uid: authStore.currentUser!.uid,
      created_at: serverTimestamp(),
      expires_at: expiresAt,
      claimed_by_uid: null,
      claimed_at: null,
    })
    if (typeof emailArg !== 'string') inviteEmail.value = ''
    toast.add(`Invite sent to ${email}`, 'success')
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error')
  } finally {
    sendingInvite.value = false
  }
}

async function revokeInvite(inviteId: string) {
  await deleteDoc(doc(db, 'agency_invites', inviteId))
  toast.add('Invite revoked', 'success')
}

// ── Join Requests ─────────────────────────────────────────────────────────────
const pendingRequests = ref<AgencyJoinRequest[]>([])
let requestsUnsub: Unsubscribe | null = null

function subscribeToPendingRequests() {
  if (!agencyId.value) return
  const q = query(
    collection(db, 'agency_join_requests'),
    where('agency_id', '==', agencyId.value),
    where('status', '==', 'pending'),
  )
  requestsUnsub = onSnapshot(q, (snap) => {
    pendingRequests.value = snap.docs
      .map(d => ({ request_id: d.id, ...d.data() } as AgencyJoinRequest))
      .sort((a, b) => a.requested_at.toMillis() - b.requested_at.toMillis())
  })
}

async function approveRequest(req: AgencyJoinRequest) {
  await updateDoc(doc(db, 'agency_join_requests', req.request_id), {
    status: 'approved',
    reviewed_by_uid: authStore.currentUser!.uid,
    reviewed_at: serverTimestamp(),
  })
  await updateDoc(doc(db, 'users', req.user_id), {
    agency_id: req.agency_id,
    role: 'member',
  })
  toast.add(`${req.user_display_name} approved`, 'success')
}

async function rejectRequest(req: AgencyJoinRequest) {
  await updateDoc(doc(db, 'agency_join_requests', req.request_id), {
    status: 'rejected',
    reviewed_by_uid: authStore.currentUser!.uid,
    reviewed_at: serverTimestamp(),
  })
  toast.add(`${req.user_display_name} rejected`, 'success')
}

// ── Domain Rules ──────────────────────────────────────────────────────────────
const domainRules = ref<AgencyDomainRule[]>([])
const newDomainInput = ref('')
let domainsUnsub: Unsubscribe | null = null

function subscribeToDomainRules() {
  if (!agencyId.value) return
  const q = query(
    collection(db, 'agency_domain_rules'),
    where('agency_id', '==', agencyId.value),
  )
  domainsUnsub = onSnapshot(q, (snap) => {
    domainRules.value = snap.docs
      .map(d => ({ domain: d.id, ...d.data() } as AgencyDomainRule))
      .sort((a, b) => a.domain.localeCompare(b.domain))
  })
}

async function addDomain() {
  const domain = newDomainInput.value.trim().toLowerCase()
  if (!domain || !agencyId.value) return
  if (!domain.includes('.') || domain.includes('@')) {
    toast.add('Enter a valid domain (e.g. firstonscene.org)', 'error')
    return
  }
  await setDoc(doc(db, 'agency_domain_rules', domain), {
    domain,
    agency_id: agencyId.value,
    created_by_uid: authStore.currentUser!.uid,
    created_at: serverTimestamp(),
  })
  newDomainInput.value = ''
  toast.add(`Domain ${domain} added`, 'success')
}

async function removeDomain(domain: string) {
  await deleteDoc(doc(db, 'agency_domain_rules', domain))
  toast.add(`Domain ${domain} removed`, 'success')
}

// ── Incidents tab ─────────────────────────────────────────────────────────────
const incidents = computed(() => incidentStore.incidents)

function incidentStatusClass(status: IncidentStatus): string {
  if (status === IncidentStatus.Active) return 'bg-green-900/40 text-green-400'
  if (status === IncidentStatus.Suspended) return 'bg-amber-900/40 text-amber-400'
  return 'bg-surface-container-highest text-outline'
}

function formatDate(ts: { toDate?: () => Date } | null | undefined): string {
  if (!ts || !ts.toDate) return '—'
  return ts.toDate().toLocaleDateString()
}

function openIncident(incident: Incident) {
  router.push({ name: 'incident-overview', params: { id: incident.incident_id } })
}

// ── Tab state ─────────────────────────────────────────────────────────────────
const activeTab = ref('settings')

watch(activeTab, (tab) => {
  if (tab === 'incidents') {
    if (agencyId.value) incidentStore.subscribeToIncidents(agencyId.value)
  }
})

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(() => {
  subscribeToAgency()
  subscribeToMembers()
  if (agencyId.value) rosterStore.subscribe(agencyId.value)
  subscribeToPresets()
  subscribeToObjectiveTemplates()
  subscribeToHazardTemplates()
  subscribeToPendingInvites()
  subscribeToPendingRequests()
  subscribeToDomainRules()
})

onUnmounted(() => {
  agencyUnsub?.()
  membersUnsub?.()
  presetsUnsub?.()
  objectivesUnsub?.()
  hazardsUnsub?.()
  invitesUnsub?.()
  requestsUnsub?.()
  domainsUnsub?.()
  incidentStore.unsubscribeAll()
})
</script>

<template>
  <div class="h-full flex flex-col">
    <header class="bg-surface px-6 py-4 flex items-center justify-between border-b border-outline-variant/15 shrink-0">
      <div class="flex items-center gap-3">
        <button
          class="text-on-surface-variant hover:text-on-surface transition-colors"
          @click="router.push('/incidents')"
        >
          <span class="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
        <span class="text-on-surface text-lg font-semibold tracking-wide uppercase">Agency</span>
      </div>
      <div class="flex items-center gap-3">
        <FosButton variant="tertiary" @click="router.push('/agency/stats')">
          <span class="material-symbols-outlined text-[16px]">bar_chart</span>
          Stats
        </FosButton>
        <UserAvatar />
      </div>
    </header>

    <div class="flex-1 overflow-y-auto">
    <div class="max-w-4xl mx-auto px-4 py-8">
      <!-- Tab navigation -->
      <div class="flex border-b border-outline-variant/15 mb-6">
        <button
          v-for="tab in [{ id: 'settings', label: 'Settings' }, { id: 'members', label: 'Members' }, { id: 'incidents', label: 'Incidents' }]"
          :key="tab.id"
          class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
          :class="activeTab === tab.id
            ? 'border-primary text-primary'
            : 'border-transparent text-outline hover:text-on-surface-variant'"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Settings -->
      <div v-if="activeTab === 'settings'" class="flex flex-col gap-8 max-w-lg">
        <!-- Agency name -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline">Agency Name</label>
          <div class="flex gap-2">
            <FosInput
              v-model="agencyName"
              :disabled="!isAdmin"
              class="flex-1"
              placeholder="Agency name"
            />
            <FosButton
              v-if="isAdmin"
              variant="secondary"
              :disabled="savingName"
              @click="saveName"
            >
              <span v-if="savingName" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
              <span v-else>Save</span>
            </FosButton>
          </div>
        </div>

        <!-- Logo -->
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline">Logo</label>
          <div v-if="agencyLogo" class="mb-2">
            <img :src="agencyLogo" alt="Agency logo" class="h-16 object-contain" />
          </div>
          <div v-else class="mb-2 text-sm text-outline">No logo uploaded</div>
          <div v-if="isAdmin">
            <input
              ref="logoFileInput"
              type="file"
              accept="image/jpeg,image/png"
              class="sr-only"
              :disabled="uploadingLogo"
              @change="onLogoFileChange"
            />
            <FosButton variant="secondary" :disabled="uploadingLogo" @click="logoFileInput?.click()">
              <span v-if="uploadingLogo" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
              <template v-else>
                <span class="material-symbols-outlined text-[16px]">upload</span>
                Upload Logo
              </template>
            </FosButton>
          </div>
        </div>

        <!-- Custom quals -->
        <div class="flex flex-col gap-2">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline">Custom Qualifications</label>
          <div class="flex flex-wrap gap-2 mb-2">
            <span
              v-for="qual in customQuals"
              :key="qual"
              class="font-mono text-xs uppercase tracking-widest px-2 py-0.5 bg-surface-container-highest text-on-surface-variant flex items-center gap-1"
            >
              {{ qual }}
              <button v-if="isAdmin" class="ml-1 text-outline hover:text-error transition-colors" @click="removeQual(qual)">
                <span class="material-symbols-outlined text-[12px]">close</span>
              </button>
            </span>
          </div>
          <div v-if="isAdmin" class="flex gap-2">
            <FosInput
              v-model="newQualInput"
              placeholder="Add a qualification"
              @keydown.enter="addQual"
            />
            <FosButton variant="secondary" @click="addQual">
              <span class="material-symbols-outlined text-[16px]">add</span>
            </FosButton>
          </div>
          <FosButton
            v-if="isAdmin"
            variant="secondary"
            class="self-start"
            :disabled="savingQuals"
            @click="saveQuals"
          >
            <span v-if="savingQuals" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
            <span v-else>Save Quals</span>
          </FosButton>
        </div>

              <!-- Radio channels -->
              <div v-if="isAdmin" class="flex flex-col gap-3">
                <div class="flex items-center justify-between">
                  <label class="text-[10px] font-label uppercase tracking-widest text-outline">Radio Channels</label>
                  <FosButton v-if="!showAddPresetForm" variant="secondary" size="sm" @click="openAddPreset">
                    Add Preset
                  </FosButton>
                </div>

                <!-- Preset list -->
                <div
                  v-if="channelPresets.length > 0"
                  class="border border-border overflow-hidden"
                >
                  <div
                    v-for="preset in channelPresets"
                    :key="preset.preset_id"
                    class="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0"
                  >
                    <div class="flex-1 min-w-0">
                      <span class="text-sm font-medium text-on-surface">{{ preset.name }}</span>
                      <span v-if="preset.rx_freq || preset.tx_freq" class="text-xs text-outline ml-2 font-mono">
                        <span v-if="preset.rx_freq">RX {{ preset.rx_freq }}</span>
                        <span v-if="preset.rx_freq && preset.tx_freq"> / </span>
                        <span v-if="preset.tx_freq">TX {{ preset.tx_freq }}</span>
                      </span>
                      <span
                        v-if="preset.mode"
                        class="ml-1.5 text-xs px-1.5 py-0.5 bg-surface-container-highest text-on-surface-variant font-mono"
                      >{{ preset.mode }}</span>
                    </div>
                    <button
                      class="p-1 text-outline hover:text-on-surface transition-colors"
                      @click="openEditPreset(preset)"
                    >
                      <span class="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    <button
                      class="p-1 text-outline hover:text-error transition-colors"
                      @click="confirmDeletePreset(preset)"
                    >
                      <span class="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
                <p v-else class="text-sm text-outline-variant">No presets yet. Add one to speed up incident setup.</p>

                <!-- Add / Edit form -->
                <div
                  v-if="showAddPresetForm"
                  class="border border-border p-4 flex flex-col gap-3 bg-surface-container"
                >
                  <div class="text-sm font-semibold text-on-surface">
                    {{ editingPresetId ? 'Edit Preset' : 'New Preset' }}
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <div class="col-span-2">
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Channel / System Name *</label>
                      <FosInput v-model="presetForm.name" placeholder="e.g. VTAC36R" />
                    </div>
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Mode</label>
                      <FosSelect
                        v-model="presetForm.mode"
                        :options="CHANNEL_MODE_OPTIONS"
                      />
                    </div>

                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Remarks</label>
                      <FosInput v-model="presetForm.remarks" placeholder="Optional notes" />
                    </div>
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">RX Freq</label>
                      <FosInput v-model="presetForm.rx_freq" placeholder="e.g. 155.340" />
                    </div>
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">RX Tone/NAC</label>
                      <FosInput v-model="presetForm.rx_tone" placeholder="e.g. 100.0" />
                    </div>
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">TX Freq</label>
                      <FosInput v-model="presetForm.tx_freq" placeholder="e.g. 155.340" />
                    </div>
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">TX Tone/NAC</label>
                      <FosInput v-model="presetForm.tx_tone" placeholder="e.g. 100.0" />
                    </div>
                  </div>
                  <div class="flex gap-2 justify-end">
                    <FosButton variant="secondary" size="sm" @click="cancelPresetForm">Cancel</FosButton>
                    <FosButton
                      size="sm"
                      :disabled="!presetForm.name.trim()"
                      @click="savePreset"
                    >{{ editingPresetId ? 'Save Changes' : 'Add Preset' }}</FosButton>
                  </div>
                </div>
              </div>
              <!-- Objective templates -->
              <div v-if="isAdmin" class="flex flex-col gap-3">
                <div class="flex items-center justify-between">
                  <label class="text-[10px] font-label uppercase tracking-widest text-outline">Objective Templates</label>
                  <FosButton v-if="!showAddObjectiveForm" variant="secondary" size="sm" @click="openAddObjective">
                    Add Template
                  </FosButton>
                </div>

                <!-- Template list -->
                <div
                  v-if="objectiveTemplates.length > 0"
                  class="border border-border overflow-hidden"
                >
                  <div
                    v-for="t in objectiveTemplates"
                    :key="t.id"
                    class="flex items-start gap-3 px-4 py-3 border-b border-border last:border-b-0"
                  >
                    <div class="flex-1 min-w-0">
                      <p class="text-sm text-on-surface">{{ t.text }}</p>
                      <div v-if="t.mission_types.length > 0" class="flex flex-wrap gap-1 mt-1">
                        <span
                          v-for="mt in t.mission_types"
                          :key="mt"
                          class="text-xs px-1.5 py-0.5 bg-surface-container-highest text-on-surface-variant font-mono"
                        >{{ missionTypeLabel(mt) }}</span>
                      </div>
                      <span v-else class="text-xs text-outline-variant">All mission types</span>
                    </div>
                    <button
                      class="p-1 text-outline hover:text-on-surface transition-colors"
                      @click="openEditObjective(t)"
                    >
                      <span class="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    <button
                      class="p-1 text-outline hover:text-error transition-colors"
                      @click="confirmDeleteObjective(t)"
                    >
                      <span class="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
                <p v-else class="text-sm text-outline-variant">No templates yet. Add one to speed up incident setup.</p>

                <!-- Add / Edit form -->
                <div
                  v-if="showAddObjectiveForm"
                  class="border border-border p-4 flex flex-col gap-3 bg-surface-container"
                >
                  <div class="text-sm font-semibold text-on-surface">
                    {{ editingObjectiveId ? 'Edit Template' : 'New Template' }}
                  </div>
                  <div class="flex flex-col gap-3">
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Objective Text *</label>
                      <FosTextarea
                        v-model="objectiveForm.text"
                        placeholder="e.g. Establish ICP at the trailhead parking lot"
                        :rows="2"
                      />
                    </div>
                    <div>
                      <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Applicable Mission Types (leave blank for all)</label>
                      <DropdownMenuRoot>
                        <DropdownMenuTrigger class="flex items-center justify-between w-full bg-surface-container-high text-on-surface font-mono text-sm px-3 py-2 focus:outline-none hover:bg-surface-container-highest transition-colors">
                          <span v-if="objectiveForm.mission_types.length === 0" class="text-outline/50">All mission types</span>
                          <span v-else class="text-primary">{{ objectiveForm.mission_types.length }} type{{ objectiveForm.mission_types.length > 1 ? 's' : '' }} selected</span>
                          <span class="material-symbols-outlined text-outline text-[18px] ml-2 shrink-0">expand_more</span>
                        </DropdownMenuTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuContent
                            class="z-50 bg-surface-container-high border border-border min-w-[var(--radix-dropdown-menu-trigger-width)] shadow-xl"
                            :side-offset="2"
                          >
                            <DropdownMenuCheckboxItem
                              v-for="opt in MISSION_TYPE_OPTIONS"
                              :key="opt.value"
                              :checked="objectiveForm.mission_types.includes(opt.value)"
                              class="flex items-center gap-2 px-3 py-1.5 font-mono text-xs text-on-surface hover:bg-surface-container-highest focus:bg-surface-container-highest cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
                              @update:checked="() => toggleObjectiveMissionType(opt.value)"
                            >
                              <DropdownMenuItemIndicator>
                                <span class="material-symbols-outlined text-primary text-[14px]">check</span>
                              </DropdownMenuItemIndicator>
                              {{ opt.label }}
                            </DropdownMenuCheckboxItem>
                            <template v-if="objectiveForm.mission_types.length > 0">
                              <DropdownMenuSeparator class="h-px bg-surface-container my-1" />
                              <DropdownMenuItem
                                class="px-3 py-1.5 font-mono text-xs text-outline hover:text-on-surface hover:bg-surface-container-highest cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
                                @select="objectiveForm.mission_types = []"
                              >
                                Clear
                              </DropdownMenuItem>
                            </template>
                          </DropdownMenuContent>
                        </DropdownMenuPortal>
                      </DropdownMenuRoot>
                    </div>
                  </div>
                  <div class="flex gap-2 justify-end">
                    <FosButton variant="secondary" size="sm" @click="cancelObjectiveForm">Cancel</FosButton>
                    <FosButton
                      size="sm"
                      :disabled="!objectiveForm.text.trim()"
                      @click="saveObjective"
                    >{{ editingObjectiveId ? 'Save Changes' : 'Add Template' }}</FosButton>
                  </div>
                </div>
              </div>

              <!-- Hazard templates -->
              <div v-if="isAdmin" class="flex flex-col gap-3">
                <div class="flex items-center justify-between">
                  <label class="text-[10px] font-label uppercase tracking-widest text-outline">Hazard Templates</label>
                  <FosButton v-if="!showAddHazardForm" variant="secondary" size="sm" @click="openAddHazard">
                    Add Template
                  </FosButton>
                </div>

                <div v-if="hazardTemplates.length > 0" class="border border-border overflow-hidden">
                  <div
                    v-for="t in hazardTemplates"
                    :key="t.template_id"
                    class="flex items-start gap-3 px-4 py-3 border-b border-border last:border-b-0"
                  >
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-semibold text-on-surface">{{ t.hazard }}</p>
                      <p v-if="t.mitigation" class="text-xs text-outline mt-0.5">{{ t.mitigation }}</p>
                    </div>
                    <button class="p-1 text-outline hover:text-on-surface transition-colors" @click="openEditHazard(t)">
                      <span class="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    <button class="p-1 text-outline hover:text-error transition-colors" @click="confirmDeleteHazard(t)">
                      <span class="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
                <p v-else class="text-sm text-outline-variant">No hazard templates yet. Add one to speed up IAP setup.</p>

                <div v-if="showAddHazardForm" class="border border-border p-4 flex flex-col gap-3 bg-surface-container">
                  <div class="text-sm font-semibold text-on-surface">
                    {{ editingHazardId ? 'Edit Hazard Template' : 'New Hazard Template' }}
                  </div>
                  <div>
                    <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Hazard *</label>
                    <FosInput v-model="hazardForm.hazard" placeholder="e.g. Unstable terrain" />
                  </div>
                  <div>
                    <label class="text-[10px] font-label uppercase tracking-widest text-outline mb-1 block">Mitigation</label>
                    <FosTextarea v-model="hazardForm.mitigation" placeholder="e.g. Brief all teams on terrain hazards before deployment" :rows="2" />
                  </div>
                  <div class="flex gap-2 justify-end">
                    <FosButton variant="secondary" size="sm" @click="cancelHazardForm">Cancel</FosButton>
                    <FosButton size="sm" :disabled="!hazardForm.hazard.trim()" @click="saveHazardTemplate">
                      {{ editingHazardId ? 'Save Changes' : 'Add Template' }}
                    </FosButton>
                  </div>
                </div>
              </div>
      </div>

      <!-- Members -->
      <div v-if="activeTab === 'members'">

        <!-- D4H roster import (admin only) -->
        <D4HRosterPanel v-if="isAdmin" :agency-id="agencyId" :custom-quals="customQuals" />

        <!-- Invite by Email (admin only) -->
        <div v-if="isAdmin" class="flex flex-col gap-3 mb-6">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline">Invite by Email</label>
          <div class="flex gap-2">
            <FosInput v-model="inviteEmail" placeholder="email@example.com" type="email" class="flex-1" />
            <FosButton variant="secondary" :disabled="sendingInvite || !inviteEmail.trim()" @click="sendInvite()">
              <span v-if="sendingInvite" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin rounded-full" />
              <span v-else>Send Invite</span>
            </FosButton>
          </div>
        </div>

        <!-- Pending Requests (admin only, hidden when empty) -->
        <div v-if="isAdmin && pendingRequests.length > 0" class="mb-6">
          <div class="flex items-center gap-2 mb-2">
            <label class="text-[10px] font-label uppercase tracking-widest text-outline">Pending Requests</label>
            <span class="font-mono text-xs px-1.5 py-0.5 bg-amber-900/30 text-amber-400">
              {{ pendingRequests.length }}
            </span>
          </div>
          <div class="border border-border overflow-hidden">
            <div
              v-for="req in pendingRequests"
              :key="req.request_id"
              class="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm text-on-surface">{{ req.user_display_name }}</p>
                <p class="text-xs text-on-surface-variant font-mono">{{ req.user_email }}</p>
                <p class="text-xs text-outline">Requested {{ formatDate(req.requested_at) }}</p>
              </div>
              <div class="flex gap-2 shrink-0">
                <FosButton variant="secondary" size="sm" @click="approveRequest(req)">Approve</FosButton>
                <FosButton variant="tertiary" size="sm" class="text-error" @click="rejectRequest(req)">Reject</FosButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Invites (admin only, hidden when empty) -->
        <div v-if="isAdmin && pendingInvites.length > 0" class="mb-6">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline block mb-2">Pending Invites</label>
          <div class="border border-border overflow-hidden">
            <div
              v-for="inv in pendingInvites"
              :key="inv.invite_id"
              class="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm text-on-surface font-mono">{{ inv.email }}</p>
                <p class="text-xs text-outline">Sent {{ formatDate(inv.created_at) }} · Expires {{ formatDate(inv.expires_at) }}</p>
              </div>
              <FosButton variant="tertiary" size="sm" class="text-error shrink-0" @click="revokeInvite(inv.invite_id)">
                Revoke
              </FosButton>
            </div>
          </div>
        </div>

        <!-- Allowed Domains (admin only) -->
        <div v-if="isAdmin" class="mb-6">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline block mb-2">Allowed Domains</label>
          <div class="flex gap-2 mb-3">
            <FosInput v-model="newDomainInput" placeholder="countysar.org" class="flex-1" @keydown.enter="addDomain" />
            <FosButton variant="secondary" :disabled="!newDomainInput.trim()" @click="addDomain">Add Domain</FosButton>
          </div>
          <div v-if="domainRules.length > 0" class="border border-border overflow-hidden">
            <div
              v-for="rule in domainRules"
              :key="rule.domain"
              class="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0"
            >
              <p class="flex-1 text-sm text-on-surface font-mono">{{ rule.domain }}</p>
              <FosButton variant="tertiary" size="sm" class="text-error shrink-0" @click="removeDomain(rule.domain)">
                Remove
              </FosButton>
            </div>
          </div>
          <p v-else class="text-xs text-outline">No domains configured.</p>
        </div>

        <div v-if="isAdmin" class="h-px bg-outline-variant/15 mb-6" />

        <!-- Members: FoS accounts and D4H roster in one list -->
        <AgencyMembersTable
          :users="members"
          :roster="rosterStore.members"
          :invited-emails="pendingInvites.map((i) => i.email)"
          :d4h-web-url="d4hWebUrl"
          :is-admin="isAdmin"
          :current-user-id="currentUserId"
          @change-role="changeRole"
          @deactivate="confirmDeactivate"
          @reactivate="reactivate"
          @invite="sendInvite"
        />
      </div>

      <!-- Incidents -->
      <div v-if="activeTab === 'incidents'">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Name</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Number</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Status</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Created</th>
                <th class="w-10" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="incident in incidents"
                :key="incident.incident_id"
                class="border-b border-outline-variant/15 last:border-0"
              >
                <td class="px-3 py-2.5 text-on-surface">{{ incident.incident_name }}</td>
                <td class="px-3 py-2.5 text-on-surface-variant font-mono text-xs">{{ incident.incident_number ?? '—' }}</td>
                <td class="px-3 py-2.5">
                  <span
                    class="font-mono text-xs uppercase tracking-widest px-2 py-0.5"
                    :class="incidentStatusClass(incident.status)"
                  >
                    {{ incident.status }}
                  </span>
                </td>
                <td class="px-3 py-2.5 text-on-surface-variant text-xs">{{ formatDate(incident.created_at) }}</td>
                <td class="px-3 py-2.5">
                  <button
                    class="text-outline hover:text-on-surface transition-colors"
                    @click="openIncident(incident)"
                  >
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Deactivate confirm dialog -->
    <FosAlertDialog
      :open="deactivateDialogOpen"
      title="Deactivate Member"
      :description="pendingDeactivateMember ? `Deactivate ${memberFullName(pendingDeactivateMember)}? They will be signed out immediately.` : ''"
      confirm-label="Deactivate"
      cancel-label="Cancel"
      variant="destructive"
      @update:open="deactivateDialogOpen = $event"
      @confirm="handleDeactivateConfirm"
      @cancel="deactivateDialogOpen = false"
    />
    </div>
  </div>
</template>
